import { NavLink, Outlet, Link, Navigate, useSearchParams, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CalendarDays, CalendarCheck, Zap, Menu, Plus, ShieldCheck, User, Users, Settings, LogOut, Bell, FileText, Sparkles, UserRound, Link2, GraduationCap, LifeBuoy, Eye, Lock } from "lucide-react";
import { WelcomeTour } from "./WelcomeTour";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "./ui/dropdown-menu";
import { useApp } from "../context/AppContext";
import { api, errMsg } from "../lib/api";
import { StructureSwitcher } from "./StructureSwitcher";

const TABS = [
  { to: "/planning", label: "Planning", icon: CalendarDays, id: "planning" },
  { to: "/disponibilites", label: "Disponibilités", icon: CalendarCheck, id: "disponibilites", badge: "availability" },
  { to: "/missions", label: "Missions", icon: Zap, id: "missions", badge: "missions" },
];

function SupportBanner() {
  const { me, current, reloadMe } = useApp();
  const nav = useNavigate();
  const back = async () => { await api.post(`/admin/support-requests/${me.support.id}/close`).catch(() => {}); await reloadMe(); nav(`/admin/comptes/${current.id}`); };
  return (
    <div className="sticky top-0 z-[60] flex flex-wrap items-center gap-2 bg-[#132F3A] px-4 py-2 text-sm text-white" data-testid="support-banner">
      <Eye className="h-4 w-4 text-[#A5BAA5]" /><span className="flex-1">Mode support · <b>{current.name}</b> · lecture seule · jusqu'à {new Date(me.support.expires_at).toLocaleTimeString("fr-BE", { timeStyle: "short" })}</span>
      <button onClick={back} className="rounded-lg bg-white/15 px-3 py-1.5 font-semibold" data-testid="support-exit-btn">Revenir à l'administration</button>
    </div>
  );
}

function Suspended() {
  const { current, logout, structures, setSid } = useApp();
  const other = structures.find((s) => s.id !== current.id && !s.suspended);
  return (
    <div className="mx-auto max-w-md px-5 pt-16 text-center" data-testid="suspended-screen">
      <Lock className="mx-auto h-12 w-12 text-[#24616A]" />
      <h1 className="mt-5 text-2xl font-bold">Compte suspendu</h1>
      <p className="mt-2 text-slate-600">L'espace « {current.name} » est temporairement suspendu. Vos données sont conservées.</p>
      <p className="mt-4 text-slate-600">Contactez le support AVELIS :<br /><a href="mailto:contact@avelis.be" className="font-semibold text-teal-700">contact@avelis.be</a> · <a href="tel:+32489567888" className="font-semibold text-teal-700">+32 489 56 78 88</a></p>
      {other && <button className="btn-ghost mt-6 w-full" onClick={() => setSid(other.id)} data-testid="suspended-switch-btn">Aller à « {other.name} »</button>}
      <button className="mt-4 py-3 text-sm font-semibold text-slate-500" onClick={logout}>Se déconnecter</button>
    </div>
  );
}

function Onboarding() {
  const { user, logout, reloadMe, setSid } = useApp();
  const nav = useNavigate();
  const [link, setLink] = useState("");
  const solo = async () => {
    try {
      const { data } = await api.post("/structures", { name: `Planning de ${user.first_name}`, type: "individuel" });
      await reloadMe();
      setSid(data.id);
      nav("/planning");
    } catch (e) { toast.error(errMsg(e)); }
  };
  const join = (e) => {
    e.preventDefault();
    const m = link.match(/invite\/([A-Za-z0-9_-]+)/);
    if (!m) return toast.error("Collez le lien d'invitation reçu");
    nav(`/invite/${m[1]}`);
  };
  return (
    <div className="mx-auto max-w-md px-5 pt-12" data-testid="onboarding-screen">
      <img src="/brand/avelis-logo.png" alt="AVELIS" className="w-[150px]" />
      <h1 className="mt-8 text-2xl font-bold">Bienvenue {user?.first_name}</h1>
      <p className="mt-1 text-slate-500">Comment souhaitez-vous commencer ?</p>
      <div className="mt-6 grid gap-3">
        <button className="btn-primary !min-h-[60px]" onClick={() => nav("/structure/nouvelle")} data-testid="onboarding-create-btn"><Users className="h-5 w-5" /> Créer une équipe</button>
        <form onSubmit={join} className="card space-y-2 p-4">
          <div className="flex items-center gap-2 font-semibold"><Link2 className="h-4 w-4 text-teal-600" /> Rejoindre une équipe</div>
          <input className="field" placeholder="Collez le lien d'invitation" value={link} onChange={(e) => setLink(e.target.value)} data-testid="onboarding-join-input" />
          <button className="btn-soft w-full" data-testid="onboarding-join-btn">Rejoindre</button>
        </form>
        <button className="btn-ghost !min-h-[60px]" onClick={solo} data-testid="onboarding-solo-btn"><UserRound className="h-5 w-5" /> Utiliser seule (mode individuel)</button>
        {user?.is_superadmin && <button className="btn-ghost" onClick={() => nav("/admin")} data-testid="onboarding-admin-btn"><ShieldCheck className="h-5 w-5" /> Administration</button>}
        <button className="py-3 text-sm font-semibold text-slate-500" onClick={logout} data-testid="onboarding-logout-btn">Se déconnecter</button>
      </div>
    </div>
  );
}

function UserMenu() {
  const { logout, badges, user } = useApp();
  const nav = useNavigate();
  const items = [["/compte", User, "Mon profil", "menu-profile"], ["/equipe", Users, "Mon équipe", "menu-team"],
    ["/notifications", Bell, `Notifications${badges.notifications ? ` (${badges.notifications})` : ""}`, "menu-notifications"],
    ["/recap", FileText, "Récapitulatif", "menu-recap"], ["/parametres", Settings, "Paramètres", "menu-settings"],
    ["/aide", GraduationCap, "Centre d'aide et de formation", "menu-help"], ["/assistance", LifeBuoy, "Assistance", "menu-assistance"], ["/avelis", Sparkles, "Découvrir AVELIS", "menu-avelis"]];
  if (user?.is_superadmin) items.push(["/admin", ShieldCheck, "Administration", "menu-admin"]);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="relative rounded-full p-3 text-slate-700 hover:bg-slate-100" data-testid="header-menu-btn" aria-label="Menu">
          <Menu className="h-5 w-5" />
          {badges.notifications > 0 && <span className="absolute right-2 top-2 h-2.5 w-2.5 rounded-full bg-red-500" />}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-60 rounded-xl bg-white p-1">
        {items.map(([to, Icon, l, t]) => (
          <DropdownMenuItem key={to} onClick={() => nav(to)} data-testid={t} className="min-h-[48px] cursor-pointer gap-3 rounded-lg text-base"><Icon className="h-4 w-4 text-teal-600" />{l}</DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={async () => { await logout(); nav("/login"); }} data-testid="menu-logout" className="min-h-[48px] cursor-pointer gap-3 rounded-lg text-base text-red-600"><LogOut className="h-4 w-4" />Déconnexion</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function AppShell({ bare }) {
  const { current, setSid, structures, badges, isManager, user } = useApp();
  const [params, setParams] = useSearchParams();
  const loc = useLocation();

  useEffect(() => {
    const s = params.get("s");
    if (s && structures.some((x) => x.id === s)) {
      setSid(s);
      params.delete("s");
      setParams(params, { replace: true });
    }
  }, [params, structures, setSid, setParams]);

  if (!current && !bare && user?.is_superadmin) return <Navigate to="/admin" replace />;
  if (!current && !bare) return <Onboarding />;
  if (current?.suspended && !bare) return <Suspended />;
  const showFab = current && isManager && !current.support && current.type !== "individuel" && ["/", "/missions", "/planning"].includes(loc.pathname);

  return (
    <div className="min-h-screen bg-slate-50">
      {current?.support && <SupportBanner />}
      <header className="pt-safe sticky top-0 z-40 border-b border-slate-200 bg-slate-50/95 backdrop-blur-md">
        <div className="mx-auto flex h-16 max-w-2xl items-center gap-1 px-2">
          <Link to="/" className="shrink-0 rounded-xl p-1.5" data-testid="header-home-btn" aria-label="Accueil"><img src="/icons/icon-192.png" alt="AVELIS" className="h-9 w-9 rounded-lg" /></Link>
          <div className="min-w-0 flex-1"><StructureSwitcher /></div>
          {current && <UserMenu />}
        </div>
      </header>
      {!bare && !current?.support && <WelcomeTour />}
      <main className="mx-auto max-w-2xl px-4 pb-36 pt-6">
        <Outlet key={current?.id || "none"} />
      </main>
      {showFab && (
        <Link to="/missions/nouvelle" data-testid="fab-new-mission" className="fixed bottom-24 right-4 z-50 flex h-14 items-center gap-2 rounded-full bg-teal-600 px-5 font-bold text-white shadow-lg shadow-teal-900/20 transition-transform active:scale-95">
          <Plus className="h-5 w-5" /> Mission
        </Link>
      )}
      {current && (
        <nav className="pb-safe fixed bottom-0 left-0 right-0 z-50 border-t border-slate-200 bg-white">
          <div className="mx-auto flex h-[68px] max-w-2xl items-stretch justify-around">
            {TABS.map((t) => (
              <NavLink key={t.id} to={t.to} data-testid={`bottom-nav-${t.id}`}
                className={({ isActive }) => `flex flex-1 flex-col items-center justify-center gap-1 text-xs font-semibold transition-colors ${isActive ? "text-teal-900" : "text-slate-500"}`}>
                {({ isActive }) => (
                  <>
                    <span className={`relative rounded-full px-5 py-1.5 ${isActive ? "bg-teal-100" : ""}`}>
                      <t.icon className="h-6 w-6" />
                      {t.badge && !current.support && badges[t.badge] > 0 && (
                        <span className="absolute right-2 top-0.5 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">
                          {t.badge === "availability" ? "!" : badges[t.badge]}
                        </span>
                      )}
                    </span>
                    {t.label}
                  </>
                )}
              </NavLink>
            ))}
          </div>
        </nav>
      )}
    </div>
  );
}
